import React from "react";

export const useMediaPermission = (localRef, pcRef, addLog) => {
  const [permission, setPermission] = React.useState("unknown"); // 'unknown' | 'granted' | 'denied'

  /**
   * Requests camera and microphone permissions from the browser.
   * Uses navigator.mediaDevices.getUserMedia and handles various error states.
   */
  const handleRequestMedia = React.useCallback(async () => {
    addLog("Requesting camera/microphone permission...");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const msg =
        "MediaDevices API not available. This might be due to an insecure context (HTTP instead of HTTPS) or an unsupported browser.";
      addLog(msg);
      setPermission("denied");
      alert(msg);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      if (stream) {
        setPermission("granted");
        localRef.current.srcObject = stream;
        addLog("Media acquired");
      }

      // If a peer connection already exists, add the new media tracks to it
      if (pcRef.current) {
        stream
          .getTracks()
          .forEach((track) => pcRef.current.addTrack(track, stream));
      }
      return stream;
    } catch (error) {
      console.error("User media error:", error.name, error.message);
      setPermission("denied");
      let userMsg = "Failed to access camera/microphone.";
      if (error.name === "NotAllowedError") {
        userMsg =
          "Permission denied by user. Please enable camera/mic access in browser settings.";
      } else if (error.name === "NotFoundError") {
        userMsg = "No camera or microphone found on this device.";
      } else if (error.name === "NotReadableError") {
        userMsg =
          "Hardware error or device is already in use by another application.";
      } else if (error.name === "SecurityError") {
        userMsg = "Security error: insecure context or origin not allowed.";
      }
      addLog(`Error :${error.name} - ${error.message}`);
      alert(
        userMsg +
          "\n\nNote: WebRTC requires HTTPS unless using localhost. If testing on a network, use mkcert/Caddy for HTTPS.",
      );
      throw error;
    }
  }, [localRef, pcRef, addLog]);
  return { permission, handleRequestMedia, setPermission };
};
